/**
 * The four states the reservations list can be in — loading, failed, empty,
 * populated. Extracted from `ReservationsPanel` (BON-472).
 *
 * The empty state distinguishes "no matches" from "none at all": telling a CSR
 * there are no reservations when their search simply missed one sends them off to
 * create a duplicate.
 */
import { Loader2 } from 'lucide-react';

import type { ReservationRow } from '../../api/reservations';

import { ReservationTableRow } from './ReservationTableRow';
import type { ReservationsListState } from './useReservationsList';

const CARD = 'rounded-xl border border-stone-200 bg-white dark:border-stone-700 dark:bg-[#1a1f1c]';

/** Three different facts, and confusing them costs a duplicate reservation: a
 *  search that missed, a history view with nothing in it, and a genuinely quiet
 *  default view. */
function emptyMessage(s: ReservationsListState): string {
  if (s.search) return 'No reservations match your search.';
  if (s.includeInactive) return 'No reservations at all, active or finished.';
  return 'No outstanding reservations.';
}

export function ReservationsListBody({
  s,
  canWrite,
  onResend,
  onEdit,
  onRelease,
}: {
  s: ReservationsListState;
  canWrite: boolean;
  onResend: (r: ReservationRow) => void;
  onEdit: (r: ReservationRow) => void;
  onRelease: (r: ReservationRow) => void;
}) {
  if (s.loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-stone-400" />
      </div>
    );
  }
  if (s.error) {
    return (
      <div className={`${CARD} py-16 text-center text-sm text-red-600 dark:text-red-400`}>
        {s.error}
      </div>
    );
  }
  if (s.reservations.length === 0) {
    return (
      <div className={`${CARD} py-16 text-center text-sm text-stone-500 dark:text-stone-400`}>
        {emptyMessage(s)}
      </div>
    );
  }
  return (
    <div className={`overflow-x-auto ${CARD}`}>
      <table className="w-full">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-stone-400">
            <th className="px-4 py-3 font-medium">Hold</th>
            <th className="px-4 py-3 font-medium">Customer</th>
            <th className="px-4 py-3 font-medium">Workshop</th>
            <th className="px-4 py-3 text-center font-medium">Seats</th>
            <th className="px-4 py-3 font-medium">Stage</th>
            <th className="px-4 py-3 font-medium">Expires in</th>
            <th className="px-4 py-3 text-right font-medium">Value</th>
            {/* No heading without buttons under it: a read-only viewer used to
                get an Actions column of empty cells. */}
            {canWrite && <th className="px-4 py-3 text-right font-medium">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {s.reservations.map((r) => (
            <ReservationTableRow
              key={r.id}
              row={r}
              canWrite={canWrite}
              onResend={() => onResend(r)}
              onEdit={() => onEdit(r)}
              onRelease={() => onRelease(r)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
